import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Card, Button, 
  Form, Alert, Spinner, Image, Modal
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from './components/AdminNavbar';
import { authAPI, aboutAPI, UPLOAD_BASE_URL } from '../../services/api';

const AdminAbout = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageType, setImageType] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    mission: '',
    vision: '',
    objectives: '',
    history: '',
    achievements: '',
    leadership_message: '',
    logo: '',
    campus_image: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const currentUser = authAPI.getCurrentUser();
    if (!currentUser) {
      navigate('/admin/login');
      return;
    }
    setUser(currentUser);
    fetchAboutData();
  }, [navigate]);

  const fetchAboutData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await aboutAPI.getForAdmin();
      
      if (result && result.success === true) {
        setFormData({
          title: result.data.title || '',
          description: result.data.description || '',
          mission: result.data.mission || '',
          vision: result.data.vision || '',
          objectives: result.data.objectives || '',
          history: result.data.history || '',
          achievements: result.data.achievements || '',
          leadership_message: result.data.leadership_message || '',
          logo: result.data.logo || '',
          campus_image: result.data.campus_image || ''
        });
      } else {
        setError(result?.message || 'Failed to fetch about data');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError(error.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (!formData.mission.trim()) {
      errors.mission = 'Mission is required';
    }
    
    if (!formData.vision.trim()) {
      errors.vision = 'Vision is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const result = await aboutAPI.update(formData);
      
      if (result && result.success === true) {
        setSuccess('About page updated successfully');
        fetchAboutData(); // Refresh data
      } else {
        setError(result?.message || 'Failed to update about page');
      }
    } catch (error) {
      setError(error.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (type) => {
    setImageType(type);
    setShowImageModal(true);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const result = await aboutAPI.uploadImage(imageType, file);
      
      if (result && result.success === true) {
        setFormData(prev => ({
          ...prev,
          [imageType === 'logo' ? 'logo' : 'campus_image']: result.data.image_path
        }));
        setSuccess(`${imageType} image uploaded successfully`);
        setShowImageModal(false);
      } else {
        setError(result?.message || 'Failed to upload image');
      }
    } catch (error) {
      setError(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all changes?')) {
      fetchAboutData();
    }
  };

  if (!user) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Checking authentication...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminNavbar onLogout={handleLogout} user={user} />
      
      <Container fluid className="py-4 admin-content">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="fw-bold mb-0">
              <i className="fas fa-info-circle me-2 text-primary"></i>
              About Page Management
            </h2>
            <p className="text-muted mb-0">Manage college about page content</p>
          </Col>
          <Col className="text-end">
            <Button 
              variant="outline-secondary" 
              onClick={fetchAboutData}
              disabled={loading}
              className="me-2"
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh
            </Button>
            <Button 
              variant="outline-danger" 
              onClick={handleReset}
              disabled={loading || saving}
            >
              <i className="fas fa-undo me-2"></i>
              Reset
            </Button>
          </Col>
        </Row>

        {/* Messages */}
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess('')}>
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading about page data...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              {/* Left Column - Main Content */}
              <Col lg={8}>
                <Card className="shadow mb-4">
                  <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-edit me-2"></i>
                      Edit About Content
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Page Title <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.title}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.title}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Description <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        isInvalid={!!formErrors.description}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.description}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Mission <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="mission"
                            value={formData.mission}
                            onChange={handleInputChange}
                            isInvalid={!!formErrors.mission}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formErrors.mission}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Vision <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="vision"
                            value={formData.vision}
                            onChange={handleInputChange}
                            isInvalid={!!formErrors.vision}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formErrors.vision}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Objectives (one per line)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="objectives"
                        value={formData.objectives}
                        onChange={handleInputChange}
                        placeholder="Enter each objective on a new line"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>History</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="history"
                        value={formData.history}
                        onChange={handleInputChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Achievements (bullet points)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="achievements"
                        value={formData.achievements}
                        onChange={handleInputChange}
                        placeholder="Enter each achievement on a new line, use • for bullets"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Leadership Message</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="leadership_message"
                        value={formData.leadership_message}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              {/* Right Column - Images & Actions */}
              <Col lg={4}>
                {/* Logo Upload */}
                <Card className="shadow mb-4">
                  <Card.Header>
                    <h6 className="mb-0">
                      <i className="fas fa-image me-2"></i>
                      College Logo
                    </h6>
                  </Card.Header>
                  <Card.Body className="text-center">
                    {formData.logo ? (
                      <div className="mb-3">
                        <Image 
                          // src={formData.logo} 
                          src={`${UPLOAD_BASE_URL}/${formData.logo}`}
                          alt="College Logo" 
                          fluid 
                          style={{ maxHeight: '150px' }}
                        />
                        <p className="small text-muted mt-2">Current Logo</p>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <i className="fas fa-university fa-3x text-muted"></i>
                        <p className="text-muted mt-2">No logo uploaded</p>
                      </div>
                    )}
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleImageUpload('logo')}
                    >
                      <i className="fas fa-upload me-2"></i>
                      {formData.logo ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                  </Card.Body>
                </Card>

                {/* Campus Image */}
                <Card className="shadow mb-4">
                  <Card.Header>
                    <h6 className="mb-0">
                      <i className="fas fa-school me-2"></i>
                      Campus Image
                    </h6>
                  </Card.Header>
                  <Card.Body className="text-center">
                    {formData.campus_image ? (
                      <div className="mb-3">
                        <Image
  src={`${UPLOAD_BASE_URL}/${formData.campus_image}`}
  alt="Campus"
  fluid
  style={{ maxHeight: '150px' }}
/>
                        <p className="small text-muted mt-2">Current Campus Image</p>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <i className="fas fa-image fa-3x text-muted"></i>
                        <p className="text-muted mt-2">No campus image</p>
                      </div>
                    )}
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleImageUpload('campus')}
                    >
                      <i className="fas fa-upload me-2"></i>
                      {formData.campus_image ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </Card.Body>
                </Card>

                {/* Save Button */}
                <Card className="shadow">
                  <Card.Body>
                    <Button 
                      variant="primary" 
                      type="submit" 
                      className="w-100"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Changes
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      className="w-100 mt-2"
                      onClick={() => window.open('/about', '_blank')}
                    >
                      <i className="fas fa-eye me-2"></i>
                      Preview Page
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Container>

      {/* Image Upload Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-upload me-2"></i>
            Upload {imageType === 'logo' ? 'Logo' : 'Campus Image'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <i className="fas fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
            <p className="mb-3">
              Select an image file (JPG, PNG, GIF, WebP)
              <br />
              <small className="text-muted">Max size: 5MB</small>
            </p>
            <Form.Group>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                disabled={uploading}
              />
            </Form.Group>
            {uploading && (
              <div className="mt-3">
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminAbout;